import { subscribeToInstallPrompt } from "./installPromptCapture";

export function isStandaloneDisplay() {
  return window.matchMedia?.("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

// Minimal platform check, not a full user-agent parse: classic iPhone/iPad/iPod UAs, plus
// modern iPadOS (13+), which reports itself as "MacIntel" and is only distinguishable from a
// real Mac by having touch points.
export function isIosDevice() {
  const isClassicIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isModernIpadOs = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return isClassicIos || isModernIpadOs;
}

// Shared, module-scoped "is the automatic branded install card currently dismissed" flag. Kept
// external to any single component (rather than component-local useState) so both the card
// (PwaStatusPrompts) and anything else that needs to read its visibility share one source of
// truth. The explicit "Install MedoPilot" entry points in the marketing nav and footer are
// intentionally independent of this flag -- see iosInstructionsVisible and
// alreadyInstalledNoticeVisible below, which back their own, separate UI.
//
// Intentionally in-memory only (no sessionStorage/localStorage): dismissing hides the card only
// for this render. A page refresh resets this module too, so the card is eligible to appear again
// on every fresh load until MedoPilot is actually installed.
let cardDismissed = false;
const dismissListeners = new Set<() => void>();

function notifyDismissListeners() {
  for (const listener of dismissListeners) {
    listener();
  }
}

export function getCardDismissed() {
  return cardDismissed;
}

export function subscribeToCardDismissed(listener: () => void) {
  dismissListeners.add(listener);
  return () => {
    dismissListeners.delete(listener);
  };
}

export function dismissInstallCard() {
  cardDismissed = true;
  notifyDismissListeners();
}

export function reopenInstallCard() {
  cardDismissed = false;
  notifyDismissListeners();
}

// Separate, independent shared flag for the compact iOS/iPadOS "Add to Home Screen" instructions
// shown by the explicit "Install MedoPilot" entry points (header/mobile drawer/footer). Kept apart
// from cardDismissed on purpose: the automatic guidance card and this user-triggered instructions
// UI must not affect each other's visibility -- e.g. a click from the mobile drawer entry point
// happens in a component that unmounts itself right after the click (closing the drawer), so this
// cannot live as that component's own local state either.
let iosInstructionsVisible = false;
const iosInstructionsListeners = new Set<() => void>();

function notifyIosInstructionsListeners() {
  for (const listener of iosInstructionsListeners) {
    listener();
  }
}

export function getIosInstructionsVisible() {
  return iosInstructionsVisible;
}

export function subscribeToIosInstructionsVisible(listener: () => void) {
  iosInstructionsListeners.add(listener);
  return () => {
    iosInstructionsListeners.delete(listener);
  };
}

export function showIosInstallInstructions() {
  iosInstructionsVisible = true;
  notifyIosInstructionsListeners();
}

export function hideIosInstallInstructions() {
  iosInstructionsVisible = false;
  notifyIosInstructionsListeners();
}

// Separate, independent shared flag for the compact "MedoPilot is already installed"
// acknowledgement shown when an explicit entry point is clicked while already running standalone.
// Standalone display mode is the only installed signal this app can reliably detect -- there is no
// way to know "installed but not currently running standalone" -- so this never implies more than
// that.
let alreadyInstalledNoticeVisible = false;
const alreadyInstalledNoticeListeners = new Set<() => void>();

function notifyAlreadyInstalledNoticeListeners() {
  for (const listener of alreadyInstalledNoticeListeners) {
    listener();
  }
}

export function getAlreadyInstalledNoticeVisible() {
  return alreadyInstalledNoticeVisible;
}

export function subscribeToAlreadyInstalledNoticeVisible(listener: () => void) {
  alreadyInstalledNoticeListeners.add(listener);
  return () => {
    alreadyInstalledNoticeListeners.delete(listener);
  };
}

export function showAlreadyInstalledNotice() {
  alreadyInstalledNoticeVisible = true;
  notifyAlreadyInstalledNoticeListeners();
}

export function hideAlreadyInstalledNotice() {
  alreadyInstalledNoticeVisible = false;
  notifyAlreadyInstalledNoticeListeners();
}

export function __resetPwaInstallVisibilityForTests() {
  cardDismissed = false;
  dismissListeners.clear();
  iosInstructionsVisible = false;
  iosInstructionsListeners.clear();
  alreadyInstalledNoticeVisible = false;
  alreadyInstalledNoticeListeners.clear();
}

// A genuinely new beforeinstallprompt event re-arms the card even if a previous prompt (or the
// iOS fallback) had already been dismissed. Subscribed once here at module scope so this runs
// exactly once regardless of how many components use the install-state hook built on top of this
// module, rather than once per mounted consumer.
if (typeof window !== "undefined") {
  subscribeToInstallPrompt((event) => {
    if (event) {
      reopenInstallCard();
    }
  });
}
